import { useEffect, useRef } from 'react';
import { useRemaining, usePartyUi } from './context';

/**
 * Countdown ring bound to the server deadline. Every device computes `endsAt - serverNow`, so they agree
 * to within the clock-offset estimate instead of drifting apart. Plays woodblock ticks in the last 5 seconds (a room cue: with a
 * shared TV, only the TV ticks).
 */
export function Countdown({ endsAt, startedAt, size = 64, label, quiet }: { endsAt: number | null; startedAt: number; size?: number; label?: string; quiet?: boolean }) {
  const { now, sound } = usePartyUi();
  const left = useRemaining(endsAt, now);
  const total = endsAt === null ? 1 : Math.max(1, endsAt - startedAt);
  const frac = endsAt === null ? 0 : Math.min(1, left / total);
  const secs = Math.ceil(left / 1000);
  const lastTick = useRef<number | null>(null);
  useEffect(() => {
    if (quiet || endsAt === null || secs > 5 || secs <= 0 || lastTick.current === secs) return;
    lastTick.current = secs;
    sound.play(secs === 1 ? 'tickFinal' : 'tick');
  }, [secs, quiet, endsAt, sound]);
  if (endsAt === null) return null;
  const r = 44;
  const c = 2 * Math.PI * r;
  const urgent = secs <= 3;
  return (
    <div className="relative inline-grid place-items-center shrink-0" style={{ width: size, height: size }} role="timer" aria-label={`${label ?? 'Time left'}: ${secs} seconds`}>
      <svg viewBox="0 0 100 100" width={size} height={size} className="-rotate-90">
        <circle cx="50" cy="50" r={r} fill="var(--color-ink)" stroke="var(--color-ink-3)" strokeWidth="10" />
        <circle
          cx="50" cy="50" r={r} fill="none" strokeWidth="10" strokeLinecap="round"
          stroke={urgent ? 'var(--color-bad)' : 'var(--accent)'}
          strokeDasharray={c} strokeDashoffset={c * (1 - frac)}
          style={{ transition: 'stroke-dashoffset 0.1s linear' }}
        />
      </svg>
      <span className={`absolute font-display font-black tabular-nums ${urgent ? 'text-bad animate-pulse' : ''}`} style={{ fontSize: size * 0.36 }}>
        {secs}
      </span>
    </div>
  );
}

/** Thin horizontal bar version for headers. */
export function TimeBar({ endsAt, startedAt }: { endsAt: number | null; startedAt: number }) {
  const { now } = usePartyUi();
  const left = useRemaining(endsAt, now, 20);
  if (endsAt === null) return null;
  const frac = Math.min(1, left / Math.max(1, endsAt - startedAt));
  return (
    <div className="h-2 w-full rounded-full bg-ink-3 overflow-hidden" aria-hidden>
      <div className="h-full rounded-full" style={{ width: `${frac * 100}%`, background: frac < 0.25 ? 'var(--color-bad)' : 'var(--accent)', transition: 'width 0.05s linear' }} />
    </div>
  );
}
